import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { IMessage } from 'react-native-gifted-chat';

import { deriveExpertSmsPromptStage } from '@/hooks/chat/expertSmsPromptStage';
import type { SendMessageAttribution } from '@/hooks/chat/useChatSendMessage';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import {
  applyCalloutAction,
  CALLOUT_KEYS,
  getUserCalloutsQueryKey,
  getUserCalloutsQueryOptions,
  selectCallout,
  type CalloutAction,
  type UserCallout,
} from '@/services/userCallouts';
import { segmentClient } from '@/util/analytics';
import { trackExpertSmsBannerDismissed, trackExpertSmsBannerViewed } from '@/util/analyticsEvents';

interface UseExpertFirstMessageNotificationParams {
  isExpert: boolean;
  messages: IMessage[];
  conversationId: string;
  onSendMessage: (messages: IMessage[], attribution?: SendMessageAttribution) => Promise<void>;
}

/**
 * Surfaces the expert SMS opt-in nudge when a reply won't reach the expert by
 * text (no number, or `newMessage` SMS off).
 *
 * Two-stage de-escalation derived from the server-backed `expert_first_message_sms`
 * callout: the full setup modal opens the first time; dismissing it records a
 * `dismiss` action so a lighter banner shows above the composer on every
 * inquiry; dismissing the banner records a `resolve` action that silences the
 * nudge for that expert entirely. Because the state lives per-account on the
 * backend, the nudge level follows the expert across devices rather than
 * resetting per device/browser.
 */
export function useExpertFirstMessageNotification({
  isExpert,
  messages,
  conversationId,
  onSendMessage,
}: UseExpertFirstMessageNotificationParams) {
  const userId = useAtomValue(userIdAtom);

  const queryClient = useQueryClient();

  /**
   * The conversation in which the expert just dismissed the setup modal. The
   * dismissal de-escalates the callout to the `banner` stage immediately, but
   * surfacing the banner in the same view would read as the dismissal not
   * taking. Suppress it here; it surfaces on the next inquiry (a different
   * `conversationId`) or on reload (this state resets). Keyed by conversation,
   * not a mount flag, so it's correct whether the chat remounts per inquiry or
   * swaps the id in place.
   */
  const [dismissedInConversation, setDismissedInConversation] = useState<string | null>(null);

  const { data: userData } = useQuery(getCurrentUserV2QueryOptions(userId));

  const calloutsQueryOptions = getUserCalloutsQueryOptions(userId);

  const { data: callouts, isSuccess: calloutsLoaded } = useQuery(calloutsQueryOptions);

  const [showMobileSetupModal, setShowMobileSetupModal] = useState(false);

  const stage = deriveExpertSmsPromptStage(
    selectCallout(callouts, CALLOUT_KEYS.EXPERT_FIRST_MESSAGE_SMS)
  );

  const hasPhoneNumber = Boolean(userData?.mobilePhone);

  const smsNotificationsEnabled = userData?.notificationsPreferences?.newMessageSms;

  /**
   * SMS only reaches the expert for replies when they have a number AND
   * `newMessage` SMS is on. Prompt whenever either is missing — not just
   * when there's no number — so an expert with a number but the SMS
   * preference off still gets nudged (and the setup turns it on).
   */
  const smsWillReach = hasPhoneNumber && Boolean(smsNotificationsEnabled);

  const isFirstExpertMessage = isExpert && messages.length <= 1;

  /** Prefs are still loading until this is defined; never nudge on incomplete data. */
  const prefsLoaded = smsNotificationsEnabled !== undefined;

  const bannerSuppressedHere = dismissedInConversation === conversationId;

  const showSmsBanner =
    stage === 'banner' && isExpert && !smsWillReach && prefsLoaded && !bannerSuppressedHere;

  useEffect(() => {
    if (showSmsBanner) {
      trackExpertSmsBannerViewed();
    }
  }, [showSmsBanner]);

  /**
   * Records a callout transition, optimistically updating the cached callouts so
   * the banner and modal react immediately and rolling back if the request fails.
   */
  const calloutMutation = useMutation({
    mutationFn: ({ action, deferredUntil }: { action: CalloutAction; deferredUntil?: string }) =>
      applyCalloutAction(CALLOUT_KEYS.EXPERT_FIRST_MESSAGE_SMS, action, deferredUntil),
    onMutate: async ({ action }) => {
      const queryKey = getUserCalloutsQueryKey(userId);

      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<UserCallout[]>(queryKey);

      queryClient.setQueryData<UserCallout[]>(queryKey, (current = []) => {
        const existing = current.find(
          (callout) => callout.key === CALLOUT_KEYS.EXPERT_FIRST_MESSAGE_SMS
        );

        const now = new Date().toISOString();

        const base: UserCallout = existing ?? {
          key: CALLOUT_KEYS.EXPERT_FIRST_MESSAGE_SMS,
          shownCount: 0,
          lastShownAt: null,
          dismissedCount: 0,
          lastDismissedAt: null,
          deferredUntil: null,
          resolvedAt: null,
          context: null,
          createdAt: now,
          updatedAt: now,
        };

        const next: UserCallout = { ...base };

        if (action === 'dismiss') {
          next.dismissedCount = base.dismissedCount + 1;

          next.lastDismissedAt = now;
        } else if (action === 'resolve') {
          next.resolvedAt = now;
        }

        const others = current.filter(
          (callout) => callout.key !== CALLOUT_KEYS.EXPERT_FIRST_MESSAGE_SMS
        );

        return [...others, next];
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(getUserCalloutsQueryKey(userId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getUserCalloutsQueryKey(userId) });
    },
  });

  /**
   * Send the reply first, then surface the SMS opt-in as a secondary nudge — it
   * must never gate the reply. The modal opens only from the untouched stage, and
   * only once callout state has loaded, so an expert who already dismissed or
   * silenced the nudge is never re-prompted.
   */
  const handleSendMessageWithCheck = useCallback(
    async (newMessages: IMessage[], attribution?: SendMessageAttribution) => {
      await onSendMessage(newMessages, attribution);

      if (isFirstExpertMessage && !smsWillReach && prefsLoaded && calloutsLoaded) {
        if (stage !== 'unset') {
          return;
        }

        segmentClient.track('First Expert Message - Mobile Setup Prompt Shown', {
          hasPhoneNumber,
          smsNotificationsEnabled,
        });

        setShowMobileSetupModal(true);
      }
    },
    [
      isFirstExpertMessage,
      smsWillReach,
      prefsLoaded,
      calloutsLoaded,
      stage,
      hasPhoneNumber,
      smsNotificationsEnabled,
      onSendMessage,
    ]
  );

  const handlePhoneNumberSetup = useCallback(() => {
    segmentClient.track('First Expert Message - Mobile Setup Complete', {});

    setShowMobileSetupModal(false);
  }, []);

  const handleDismissModal = useCallback(() => {
    segmentClient.track('First Expert Message - Mobile Setup Dismissed', {});

    setShowMobileSetupModal(false);

    setDismissedInConversation(conversationId);

    calloutMutation.mutate({ action: 'dismiss' });
  }, [calloutMutation, conversationId]);

  const handleBannerTurnOn = useCallback(() => {
    setShowMobileSetupModal(true);
  }, []);

  const handleBannerDismiss = useCallback(() => {
    trackExpertSmsBannerDismissed();

    calloutMutation.mutate({ action: 'resolve' });
  }, [calloutMutation]);

  return {
    showMobileSetupModal,
    showSmsBanner,
    handleSendMessageWithCheck,
    handlePhoneNumberSetup,
    handleDismissModal,
    handleBannerTurnOn,
    handleBannerDismiss,
  };
}
